import type { PageSizeId } from "../exporters/pdfExporter";
import externalLinkRaw from "../icons/external-link.svg?raw";
import fedexRaw from "../icons/partner-fedex.svg?raw";
import staplesRaw from "../icons/partner-staples.svg?raw";
import { stripIconSvg } from "../icons/stripIconSvg";

/**
 * Third-party print services (issue #289) that can output sizes most people
 * can't print at home, keyed by the paper sizes each offering covers. This is
 * configuration only — `AdvancedPrintDialog` renders a button per service that
 * matches the chosen paper size, opening the service in a new tab.
 *
 * Brand wordmarks are approximated with the system font in the brand colours so
 * no logo asset has to be bundled. They live in `src/icons/partner-*.svg` and
 * come through the shared {@link stripIconSvg} like every other UI image
 * (#216); their rendered size is `.print-partner-*` in styles.css, since the
 * strip removes hard-coded `width` / `height`.
 */

const FEDEX_LOGO = stripIconSvg(fedexRaw);
const STAPLES_LOGO = stripIconSvg(staplesRaw);

/** "Opens in a new tab" external-link glyph (inherits the link's text colour). */
const EXTERNAL_LINK_ICON = stripIconSvg(externalLinkRaw);

export interface PrintService {
  /** Short partner name shown in the paper-size dropdown (e.g. "FedEx"). */
  partner: string;
  /** Paper sizes this offering covers. */
  sizes: PageSizeId[];
  /** Call-to-action wording shown above the buttons, matched to the service
   *  kind (e.g. "Order blueprints printed from:" vs "Order document prints
   *  from:"). Services that share a paper size are expected to share a prompt. */
  prompt: string;
  url: string;
  /** Tooltip / accessible label. */
  title: string;
  logo: string;
}

const DOCUMENT_PROMPT = "Order document prints from:";
const BLUEPRINT_PROMPT = "Order blueprints printed from:";

export const PRINT_SERVICES: PrintService[] = [
  // FedEx Office
  {
    partner: "FedEx",
    sizes: ["letter", "tabloid"],
    prompt: DOCUMENT_PROMPT,
    url: "https://www.office.fedex.com/default/copies.html",
    title: "FedEx Office — copies (8.5×11″ & 11×17″)",
    logo: FEDEX_LOGO,
  },
  {
    partner: "FedEx",
    sizes: ["arch-c", "arch-d", "arch-e", "arch-e1"],
    prompt: BLUEPRINT_PROMPT,
    url: "https://www.office.fedex.com/default/architecture-construction-engineering.html",
    title: "FedEx Office — architecture, construction & engineering printing",
    logo: FEDEX_LOGO,
  },
  // Staples
  {
    partner: "Staples",
    sizes: ["letter", "legal", "tabloid"],
    prompt: DOCUMENT_PROMPT,
    url: "https://www.staples.com/services/printing/copies-documents-printing/",
    title: "Staples — copies & document printing (8.5×11″, 8.5×14″, 11×17″)",
    logo: STAPLES_LOGO,
  },
  {
    partner: "Staples",
    sizes: ["arch-c", "arch-d", "arch-e", "arch-e1"],
    prompt: BLUEPRINT_PROMPT,
    url: "https://www.staples.com/services/printing/engineering-blueprints/",
    title: "Staples — engineering blueprint printing",
    logo: STAPLES_LOGO,
  },
];

/** Services offering the given paper size, in declared order. */
export function printServicesFor(pageSize: PageSizeId): PrintService[] {
  return PRINT_SERVICES.filter((s) => s.sizes.includes(pageSize));
}

/** Call-to-action wording for the given paper size, taken from the first
 *  matching service ("" when none match). */
export function printPromptFor(pageSize: PageSizeId): string {
  return printServicesFor(pageSize)[0]?.prompt ?? "";
}

/** Distinct partner names offering the given paper size, in declared order. */
export function printPartnerNamesFor(pageSize: PageSizeId): string[] {
  const names: string[] = [];
  for (const s of printServicesFor(pageSize)) {
    if (!names.includes(s.partner)) names.push(s.partner);
  }
  return names;
}

/** A service rendered as a button (logo + external-link icon) that opens in a
 *  new tab. */
function serviceButton(s: PrintService): string {
  // The wordmark and the glyph are wrapped so `.print-partner-logo svg` /
  // `.print-partner-external svg` can size each one — the shared strip takes
  // the `width` / `height` they used to carry themselves (#216).
  return `<a class="print-partner-btn" href="${s.url}" target="_blank" rel="noopener noreferrer" title="${s.title}"><span class="print-partner-logo">${s.logo}</span><span class="print-partner-external" aria-hidden="true">${EXTERNAL_LINK_ICON}</span></a>`;
}

/** Buttons HTML for every service matching `pageSize` (empty string when none,
 *  so the caller can hide the panel). */
export function renderPrintServices(pageSize: PageSizeId): string {
  return printServicesFor(pageSize).map(serviceButton).join("");
}
